import React from "react";
import { 
  FiUpload, 
  FiCheckCircle, 
  FiCopy, 
  FiUser,
  FiFileText,
  FiMessageSquare,
  FiDatabase
} from "react-icons/fi";

const features = [
  {
    icon: <FiUpload className="w-8 h-8" />,
    title: "Upload de document",
    description: "Téléversez vos documents (PDF, DOCX, TXT) via un glisser-déposer ou en cliquant sur la zone désignée.",
    color: "from-teal-500 to-emerald-500",
    bgColor: "bg-gradient-to-br from-teal-50 to-emerald-50",
    details: ["Support multi-format", "Interface drag & drop", "Taille maximale : 10MB"]
  },
  {
    icon: <FiCheckCircle className="w-8 h-8" />,
    title: "Résumé intelligent",
    description: "Notre IA analyse et génère automatiquement un résumé précis et structuré de votre document.",
    color: "from-amber-500 to-orange-500",
    bgColor: "bg-gradient-to-br from-amber-50 to-orange-50",
    details: ["Extraction des points clés", "Synthèse automatique", "Résumés structurés et clairs grâce à l'IA"]
  },
  {
    icon: <FiMessageSquare className="w-8 h-8" />,
    title: "Chat avec l'IA",
    description: "Interrogez notre assistant IA pour obtenir des explications détaillées sur le contenu de vos documents.",
    color: "from-blue-500 to-indigo-500",
    bgColor: "bg-gradient-to-br from-blue-50 to-indigo-50",
    details: ["Réponses en temps réel", "Questions contextuelles", "Support multilingue"]
  },
  {
    icon: <FiCopy className="w-8 h-8" />,
    title: "Gestion des contenus",
    description: "Copiez, exportez ou partagez facilement vos résumés et conversations avec l'IA.",
    color: "from-purple-500 to-pink-500",
    bgColor: "bg-gradient-to-br from-purple-50 to-pink-50",
    details: ["Copie instantanée", "Export PDF", "Interface intuitive"]
  },
  {
    icon: <FiDatabase className="w-8 h-8" />,
    title: "Historique des documents",
    description: "Consultez tous vos documents précédents dans une interface organisée par date.",
    color: "from-violet-500 to-purple-500",
    bgColor: "bg-gradient-to-br from-violet-50 to-purple-50",
    details: ["Recherche rapide", "Organisation chronologique", "Accès instantané"]
  },
  {
    icon: <FiUser className="w-8 h-8" />,
    title: "Gestion de compte",
    description: "Connectez-vous avec Google pour sauvegarder vos documents et accéder à votre historique.",
    color: "from-green-500 to-emerald-500",
    bgColor: "bg-gradient-to-br from-green-50 to-emerald-50",
    details: ["Connectez-vous avec Google ou créez un compte avec votre email", "Historique des documents", "Gestion de profil"]
  },
];
const ManualSection = () => {
  return (
    <section id="manual" className="relative py-16 px-4 bg-gradient-to-b from-gray-50 to-white overflow-hidden">
      {/* Background decorative elements */}
      <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-teal-500 via-blue-500 to-purple-500"></div>
      
      <div className="max-w-7xl mx-auto">
        {/* Header section */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gradient-to-r from-teal-500 to-blue-500 mb-4 shadow-lg">
            <FiFileText className="w-8 h-8 text-white" />
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            Comment ça <span className="bg-gradient-to-r from-teal-600 to-blue-600 bg-clip-text text-transparent">fonctionne</span>
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Découvrez comment SumDocx transforme vos documents en connaissances exploitables en quelques étapes simples
          </p>
        </div>

        {/* Features grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {features.map((feature, index) => (
            <div
              key={index}
              className="group relative bg-white rounded-2xl shadow-xl hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-2 overflow-hidden"
              style={{
                border: "2px solid transparent",
              }}
            >
              {/* Accent top border */}
              <div className={`h-2 w-full bg-gradient-to-r ${feature.color}`}></div>
              
              <div className="p-8">
                {/* Number indicator */}
                <div className="absolute top-6 right-6 w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center text-gray-400 font-bold text-sm group-hover:scale-110 transition-transform">
                  {index + 1}
                </div>
                
                {/* Icon */}
                <div className={`w-16 h-16 rounded-2xl ${feature.bgColor} flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300`}>
                  <div className={`text-gradient bg-gradient-to-r ${feature.color} bg-clip-text text-transparent`}>
                    {feature.icon}
                  </div>
                </div>
                
                {/* Title */}
                <h3 className="text-2xl font-bold text-gray-900 mb-3">
                  {feature.title}
                </h3>
                
                {/* Description */}
                <p className="text-gray-600 mb-6 leading-relaxed">
                  {feature.description}
                </p>
                
                {/* Details list */}
                <ul className="space-y-2">
                  {feature.details.map((detail, idx) => (
                    <li key={idx} className="flex items-center text-sm text-gray-500">
                      <div className={`w-2 h-2 rounded-full bg-gradient-to-r ${feature.color} mr-3`}></div>
                      {detail}
                    </li>
                  ))}
                </ul>
              </div>
              
              {/* Hover effect overlay - simplifié */}
              <div 
                className="absolute inset-0 rounded-2xl transition-all duration-300 pointer-events-none"
                style={{
                  background: `linear-gradient(to right, ${feature.color.replace('from-', '').replace('to-', '').replace(' ', ', ')})`,
                  opacity: 0,
                }}
              ></div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default ManualSection;